import type { FilterGroup, FilterGroupRequest, FilterOperator, FilterRow } from '../../types/contactList'
import type { GeoMetricRow } from '../../types/geoAnalytics'
import { parseDistrictCode } from '../../types/guidedList'
import type { GoalKind, GuidedListSpec, IssueKey } from '../../types/guidedList'

type BareRow = { fieldName: string; operator: FilterOperator; value?: string }

/** Append one predicate to the first group (AND). Returns a fresh copy — never mutates. */
export function withExtraRow(groups: FilterGroup[], row: BareRow): FilterGroup[] {
  return groups.map((g, i) => {
    if (i !== 0) return g
    const filters: FilterRow[] = [
      ...g.filters,
      { id: `x-${row.fieldName}-${row.value ?? ''}`, displayOrder: g.filters.length, ...row },
    ]
    return { ...g, filters }
  })
}

/** "How they lean" buckets — OUR abstraction over CatalistIdeologyPlus (0=cons..100=prog). */
export const IDEOLOGY_BANDS: { label: string; rows: BareRow[] }[] = [
  { label: 'Progressive', rows: [{ fieldName: 'CatalistIdeologyPlus', operator: 'GreaterThanOrEqual', value: '65' }] },
  { label: 'Moderate', rows: [
    { fieldName: 'CatalistIdeologyPlus', operator: 'GreaterThanOrEqual', value: '35' },
    { fieldName: 'CatalistIdeologyPlus', operator: 'LessThan', value: '65' },
  ] },
  { label: 'Conservative', rows: [{ fieldName: 'CatalistIdeologyPlus', operator: 'LessThan', value: '35' }] },
]

/** Precinct centroids weighted by aggregate universe.count. Aggregate texture, NOT individuals:
 *  suppressed (k<10) and zero/null cells are dropped so the map only glows where there's real data. */
export function buildDensityPoints(
  units: GeoJSON.FeatureCollection,
  rows: GeoMetricRow[],
): GeoJSON.FeatureCollection {
  const byUnit = new Map(rows.map(r => [r.geoUnitId, r]))
  const features: GeoJSON.Feature[] = []
  for (const u of units.features) {
    const id = u.properties?.id as string | undefined
    const centroid = u.properties?.centroid as [number, number] | undefined
    const row = id ? byUnit.get(id) : undefined
    if (!centroid || !row || row.suppressed || row.value == null || row.value <= 0) continue
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: centroid },
      properties: { weight: row.value },
    })
  }
  return { type: 'FeatureCollection', features }
}

/** The district polygon whose name trailing-int equals the wizard's districtCode. */
export function findDistrictFeature(
  units: GeoJSON.FeatureCollection,
  districtCode: string | null | undefined,
): GeoJSON.Feature | null {
  if (!districtCode) return null
  return units.features.find(
    f => parseDistrictCode(String(f.properties?.name ?? '')) === districtCode,
  ) ?? null
}

/** Spec with the goal replaced — used to count the mobilize vs persuade split from one universe. */
export function withGoal(spec: GuidedListSpec, goal: GoalKind): GuidedListSpec {
  return { ...spec, goal }
}

/** Spec scoped to a single issue — used for the per-issue "supporters in universe" line. */
export function withSingleIssue(spec: GuidedListSpec, issue: IssueKey): GuidedListSpec {
  return { ...spec, issues: spec.issues.filter(i => i.issue === issue) }
}

/** Compiled FilterGroups carry a client-side `id`; the geo/live-metrics wire type does not. */
export function toFilterGroupRequests(groups: FilterGroup[]): FilterGroupRequest[] {
  return groups.map(g => ({
    groupIndex: g.groupIndex,
    filters: g.filters.map(({ fieldName, operator, value, displayOrder }) => ({ fieldName, operator, value, displayOrder })),
  }))
}
